import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'

/**
 * Guía de setup para el equipo (admin y sistemas). Hermana de la guía pública
 * `/guia` (que es para el jugador/cliente): acá están los pasos de
 * preparación que el club no hace, con cómo verificar cada uno y qué revisar
 * si falla. Un solo recorrido: el setup de club es tablet + Polar H10 + Quest
 * con la app VerSports Sensores (mg, 2026-09-28: "tablet, polar, quest
 * nomás"). El recorrido del kit con base (RPi + GSR) salió de la UI; su
 * referencia vive en vrs-docs (34, 49) y el camino edge opt-in (ADR 0002)
 * sigue en el código, sin entrada desde acá.
 */

interface StaffStep {
  title: string
  body: ReactNode
  verify: ReactNode
  trouble?: ReactNode
}

const DOCS = 'https://github.com/institutohumai/vrs-docs/blob/main'
const docLink = (file: string, label: string) => (
  <a className="underline" href={`${DOCS}/${file}`} target="_blank" rel="noopener noreferrer">{label}</a>
)

const STEPS: StaffStep[] = [
  {
    title: 'Instalar VerSports Sensores en el visor (una vez)',
    body: <>
      <p>Hoy se instala por USB: el visor tiene que tener <strong>Modo desarrollador</strong> (organización HumaiTech de Meta) y aceptar “Permitir depuración USB”. Desde el repo <a className="underline" href="https://github.com/institutohumai/vrs-quest-sensores" target="_blank" rel="noopener noreferrer">vrs-quest-sensores</a>:</p>
      <pre className="overflow-x-auto rounded-lg bg-surface-highlight p-md text-sm"><code>./install.sh</code></pre>
      <p>Instala la app, le da el permiso de Bluetooth y la abre. Para visores de clientes, el camino previsto es el canal Alpha privado de la Meta Horizon Store: el build está subido; falta invitar cuentas y probar la instalación desde la biblioteca del visor.</p>
    </>,
    verify: <p>En el visor, la app muestra <strong>Permiso Bluetooth: Concedido</strong> y <strong>Servicio: Activo</strong>. Aparece en Biblioteca → Orígenes desconocidos.</p>,
    trouble: <p>Detalles, firma y distribución: {docLink('58-app-quest-sensores.md', 'doc 58')}.</p>,
  },
  {
    title: 'Primera apertura de VerSports',
    body: <p>En la app tocá <strong>Abrir VerSports</strong>. La primera vez el navegador pide acceso a la <strong>red local</strong>: aceptalo. Sin ese permiso la página no puede hablar con la app.</p>,
    verify: <p>En la app, <strong>VerSports: Conectado</strong>. El visor queda recordado y las próximas veces alcanza con abrir ver-sports.com.</p>,
    trouble: <p>Si se denegó el permiso, habilitalo en la configuración del sitio ver-sports.com del navegador y recargá.</p>,
  },
  {
    title: 'Colocar el Polar',
    body: <p>Electrodos húmedos, módulo en los dos broches, banda contra la piel.</p>,
    verify: <p>La tarjeta del Polar en la app pasa a <strong>Conectado</strong> con el pulso en lpm. En el vr-app, el indicador Polar pasa a Conectado y Datos fisio muestra la fuente <code>polar_direct</code>.</p>,
    trouble: <p>Si dice “No aparece el Polar”, la app lista los equipos que ve el visor. Sin contacto húmedo el Polar no transmite, y un celular con Polar Flow cerca puede tenerlo tomado: apagalo.</p>,
  },
  {
    title: 'Prueba final con el jugador',
    body: <p>Hacé una sesión corta. El biofeedback usa el pulso del Polar.</p>,
    verify: <p>Los datos siguen llegando dentro de la sesión en VR (la app en el visor muestra “último dato recién”).</p>,
    trouble: <p>Esta variante está en beta: la continuidad dentro de la sesión inmersiva todavía no se validó con un Polar real.</p>,
  },
]

export function AdminSetupGuide() {
  const steps = STEPS
  return (
    <div className="mx-auto max-w-3xl">
      <header className="mb-lg">
        <h1 className="text-2xl font-bold">Setup del equipo</h1>
        <p className="mt-sm text-text-secondary">
          Preparación de visores antes de entregarlos o de una sesión. La guía para el jugador y el club es <Link className="underline" to="/guia">/guia</Link>.
        </p>
      </header>

      <ol className="space-y-md">
        {steps.map((step, i) => (
          <li key={step.title} className="card p-lg">
            <h2 className="font-display text-lg font-bold normal-case tracking-tight">
              <span className="mr-sm text-text-muted">{i + 1}.</span>{step.title}
            </h2>
            <div className="mt-sm space-y-sm text-text-secondary">{step.body}</div>
            <section className="mt-md rounded-lg border border-primary/30 bg-primary/10 p-md" aria-label="Cómo verificar">
              <h3 className="text-sm font-semibold">Cómo verificar</h3>
              <div className="mt-xs text-sm text-text-secondary">{step.verify}</div>
            </section>
            {step.trouble && (
              <details className="mt-sm rounded-lg border border-outline p-md">
                <summary className="cursor-pointer text-sm font-semibold">Si falla</summary>
                <div className="mt-sm text-sm text-text-secondary">{step.trouble}</div>
              </details>
            )}
          </li>
        ))}
      </ol>
    </div>
  )
}
